import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { categoryName } from "@/lib/helpers";
import Seo from "@/components/Seo";
import { pageTitle, truncate } from "@/lib/seo";
import { useSiteContent, scValue } from "@/hooks/useSiteContent";

const PageHeader = ({ title, subtitle }) => (
  <section className="bg-brand-navy text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
      <h1 className="text-4xl md:text-5xl font-extrabold font-heading tracking-tight">{title}</h1>
      <p className="mt-4 text-white/70 max-w-2xl">{subtitle}</p>
    </div>
  </section>
);

const Products = () => {
  const { t, lang } = useI18n();
  const sc = useSiteContent();
  const pp = sc?.page_products;
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const activeCat = params.get("categoria") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (activeCat) q.category = activeCat;
      if (search) q.search = search;
      const { data } = await api.get("/products", { params: q });
      setProducts(data);
    } catch {
      setProducts([]);
    } finally { setLoading(false); }
  }, [activeCat, search]);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setCat = (slug) => {
    if (slug) setParams({ categoria: slug }); else setParams({});
  };

  const typeOptions = [
    { key: "all", label: t("common.all") },
    { key: "books", label: lang === "en" ? "Books" : "Libri" },
    { key: "amazon", label: lang === "en" ? "Amazon products" : "Prodotti Amazon" },
    { key: "digital", label: lang === "en" ? "Digital products" : "Prodotti digitali" },
  ];
  const matchesType = (p) => {
    if (typeFilter === "all") return true;
    const isAmazon = p.product_type === "amazon";
    const kind = p.amazon_kind || "book";
    if (typeFilter === "books") return isAmazon && kind === "book";
    if (typeFilter === "amazon") return isAmazon && kind === "product";
    if (typeFilter === "digital") return !isAmazon;
    return true;
  };
  const shown = products.filter(matchesType);

  return (
    <div data-testid="products-page">
      <Seo
        title={pageTitle(scValue(pp, "seoTitle", lang, "") || scValue(pp, "title", lang, t("products.title")))}
        description={truncate(scValue(pp, "seoDesc", lang, "") || scValue(pp, "subtitle", lang, t("products.subtitle")))}
        path="/prodotti"
        type="website"
      />
      <PageHeader title={scValue(pp, "title", lang, t("products.title"))} subtitle={scValue(pp, "subtitle", lang, t("products.subtitle"))} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex flex-wrap gap-2">
            <button data-testid="filter-all" onClick={() => setCat("")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!activeCat ? "bg-brand-electric text-white" : "bg-white border border-brand-navy/15 text-brand-navy hover:border-brand-electric"}`}>
              {t("common.all")}
            </button>
            {categories.map((c) => (
              <button key={c.slug} data-testid={`filter-${c.slug}`} onClick={() => setCat(c.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCat === c.slug ? "bg-brand-electric text-white" : "bg-white border border-brand-navy/15 text-brand-navy hover:border-brand-electric"}`}>
                {categoryName(c, lang)}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
            <Input data-testid="products-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search")} className="pl-9 rounded-full bg-white" />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="type-filter">
          <span className="text-xs uppercase tracking-widest text-brand-navy/40 mr-1">{lang === "en" ? "Type" : "Tipo"}</span>
          {typeOptions.map((o) => (
            <button key={o.key} data-testid={`filter-type-${o.key}`} onClick={() => setTypeFilter(o.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${typeFilter === o.key ? "bg-brand-navy text-white" : "bg-white border border-brand-navy/15 text-brand-navy hover:border-brand-electric"}`}>
              {o.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 text-center text-brand-navy/50">{t("common.loading")}</div>
        ) : shown.length === 0 ? (
          <div data-testid="products-empty" className="py-24 text-center text-brand-navy/50">{t("products.empty")}</div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
